// src/pages/PricingPage.jsx - Page tarifaire multi-cibles
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';

const C = {
  bg: '#0A0E1A', surface: '#111827', card: '#1a2235',
  border: '#1e2d45', accent: '#00D4AA', gold: '#F5C542',
  purple: '#7C3AED', blue: '#0EA5E9', red: '#FF4F6A',
  text: '#E8EDF5', muted: '#6b7fa3',
};

const TARGETS = [
  { key: 'personal',   label: '👤 Grand public',  desc: 'Particuliers' },
  { key: 'student',    label: '🎓 Étudiants',      desc: 'Tarif réduit' },
  { key: 'business',   label: '🏢 Entreprises',    desc: 'Équipes & PME' },
  { key: 'enterprise', label: '🏦 Grand compte',   desc: 'Sur mesure' },
];

const PLANS = {
  personal: [
    {
      code: 'free', name: 'Gratuit', price: 0, yearlyPrice: 0,
      color: C.muted, highlight: false,
      features: ['10 transactions/mois', 'Limite 500FCFA /transaction', 'Historique 30 jours', 'Support communautaire'],
      missing: ['Split bill', 'Analytics', 'Accès API'],
      fee: '1%',
    },
    {
      code: 'premium', name: 'Premium', price: 4.99, yearlyPrice: 49.99,
      color: C.accent, highlight: true, badge: '⭐ Populaire',
      features: ['Transactions illimitées', 'Limite 2 000FCFA /transaction', 'Historique 1 an', 'Support prioritaire', 'Split bill', 'Analytics personnels'],
      missing: ['Accès API'],
      fee: '0,8%',
    },
  ],
  student: [
    {
      code: 'free', name: 'Gratuit', price: 0, yearlyPrice: 0,
      color: C.muted, highlight: false,
      features: ['10 transactions/mois', 'Split bill basique', 'Historique 30 jours'],
      missing: ['Analytics', 'Accès API'],
      fee: '1%',
    },
    {
      code: 'student', name: 'Étudiant', price: 1.99, yearlyPrice: 19.99,
      color: C.blue, highlight: true, badge: '🎓 -60% étudiant',
      features: ['Transactions illimitées', 'Limite 500FCFA /transaction', 'Historique 6 mois', 'Split bill avancé', 'Support standard'],
      missing: ['Analytics', 'Accès API'],
      fee: '0,5%',
      note: 'Vérification carte étudiante requise',
    },
  ],
  business: [
    {
      code: 'business', name: 'Business', price: 19.99, yearlyPrice: 199.99,
      color: C.gold, highlight: true, badge: '🏢 Recommandé',
      features: ['Transactions illimitées', 'Limite 10 000FCFA /transaction', 'Historique 2 ans', 'Jusqu\'à 10 utilisateurs', 'Notes de frais', 'Virements groupés', 'Analytics avancés', 'Accès API', 'Support dédié'],
      missing: ['Paie illimitée', 'Marque blanche'],
      fee: '0,7%',
    },
    {
      code: 'enterprise', name: 'Entreprise', price: 99.99, yearlyPrice: 999.99,
      color: C.purple, highlight: false, badge: '🏦 Complet',
      features: ['Tout Business inclus', 'Utilisateurs illimités', 'Aucune limite de transaction', 'Paie & RH intégrés', 'Marque blanche', 'SLA 99,9%', 'Historique 10 ans', 'Gestionnaire dédié'],
      missing: [],
      fee: '0,5%',
    },
  ],
  enterprise: [
    {
      code: 'enterprise', name: 'Entreprise', price: 99.99, yearlyPrice: 999.99,
      color: C.purple, highlight: true, badge: '🏦 Standard',
      features: ['Utilisateurs illimités', 'Transactions illimitées', 'Paie & RH intégrés', 'Marque blanche', 'SLA 99,9%', 'API complète', 'Gestionnaire dédié', 'Audit & conformité'],
      missing: [],
      fee: '0,5%',
    },
    {
      code: 'custom', name: 'Sur mesure', price: null, yearlyPrice: null,
      color: C.gold, highlight: false, badge: '✨ Sur mesure',
      features: ['Tout Entreprise inclus', 'Commission négociable', 'Intégrations sur mesure', 'Hébergement dédié', 'Support 24/7', 'Formation équipes'],
      missing: [],
      fee: 'Négociable',
      cta: 'Nous contacter',
    },
  ],
};

const REVENUE_PROJECTIONS = [
  { users: 500,   volume: '50 000FCFA ',   fees: '500FCFA ',    subs: '200FCFA ',    total: '700FCFA ',    label: 'Démarrage' },
  { users: 2000,  volume: '200 000FCFA ',  fees: '2 000FCFA ',  subs: '800FCFA ',    total: '2 800FCFA ',  label: '6 mois' },
  { users: 10000, volume: '1 000 000FCFA ',fees: '10 000FCFA ', subs: '4 000FCFA ',  total: '14 000FCFA ', label: '1 an' },
  { users: 50000, volume: '5 000 000FCFA ',fees: '50 000FCFA ', subs: '20 000FCFA ', total: '70 000FCFA ', label: '3 ans' },
];

export function PricingPage() {
  const [activeTarget, setActiveTarget] = useState('personal');
  const [billing, setBilling] = useState('monthly');
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  const plans = PLANS[activeTarget] || [];

  const handleChoosePlan = (plan) => {
    if (plan.code === 'custom') {
      window.location.href = 'mailto:contact@paychat.app?subject=Offre Entreprise Sur Mesure';
      return;
    }
    if (!isAuthenticated) { navigate('/register'); return; }
    navigate('/subscribe', { state: { plan_code: plan.code, billing } });
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'Sora', sans-serif", color: C.text }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&display=swap'); *{box-sizing:border-box}`}</style>

      {/* Hero */}
      <div style={{ textAlign: 'center', padding: '80px 24px 40px' }}>
        <div style={{ display: 'inline-block', background: `${C.accent}22`, border: `1px solid ${C.accent}44`, borderRadius: 20, padding: '6px 18px', fontSize: 12, color: C.accent, fontWeight: 700, marginBottom: 20, letterSpacing: 1 }}>
          TARIFS TRANSPARENTS · COMMISSION 1%
        </div>
        <h1 style={{ fontSize: 52, fontWeight: 800, letterSpacing: '-2px', margin: '0 0 16px', background: `linear-gradient(135deg, ${C.text}, ${C.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Un plan pour chaque besoin
        </h1>
        <p style={{ fontSize: 18, color: C.muted, maxWidth: 560, margin: '0 auto 40px', lineHeight: 1.6 }}>
          Particuliers, étudiants, entreprises ou grands comptes — PayChat s'adapte à votre usage.
        </p>

        {/* Toggle mensuel/annuel */}
        <div style={{ display: 'inline-flex', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 4, gap: 4, marginBottom: 40 }}>
          {[['monthly', 'Mensuel'], ['yearly', 'Annuel (-20%)']].map(([val, label]) => (
            <button key={val} onClick={() => setBilling(val)} style={{
              padding: '8px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: billing === val ? `linear-gradient(90deg, ${C.accent}, #00b894)` : 'transparent',
              color: billing === val ? '#000' : C.muted,
              fontWeight: billing === val ? 700 : 400, fontSize: 14,
              fontFamily: "'Sora', sans-serif",
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Sélecteur de cible */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: '0 24px 40px', flexWrap: 'wrap' }}>
        {TARGETS.map(t => (
          <button key={t.key} onClick={() => setActiveTarget(t.key)} style={{
            padding: '12px 24px', borderRadius: 14,
            background: activeTarget === t.key ? C.card : 'transparent',
            border: `2px solid ${activeTarget === t.key ? C.accent : C.border}`,
            cursor: 'pointer', transition: 'all 0.2s',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: activeTarget === t.key ? C.accent : C.text, fontFamily: "'Sora', sans-serif" }}>{t.label}</div>
            <div style={{ fontSize: 11, color: C.muted, fontFamily: "'Sora', sans-serif" }}>{t.desc}</div>
          </button>
        ))}
      </div>

      {/* Plans */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 24, padding: '0 24px 80px', flexWrap: 'wrap', maxWidth: 1200, margin: '0 auto' }}>
        {plans.map(plan => {
          const price = billing === 'yearly' && plan.yearlyPrice !== null
            ? (plan.yearlyPrice / 12).toFixed(2)
            : plan.price;

          return (
            <div key={plan.code} style={{
              width: 320, background: plan.highlight ? `linear-gradient(135deg, ${plan.color}15, ${C.card})` : C.card,
              border: `2px solid ${plan.highlight ? plan.color : C.border}`,
              borderRadius: 24, padding: '32px 28px',
              boxShadow: plan.highlight ? `0 0 40px ${plan.color}22` : 'none',
              position: 'relative', transition: 'transform 0.2s',
            }}>
              {plan.badge && (
                <div style={{
                  position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                  background: plan.color, color: plan.color === C.muted ? '#fff' : '#000',
                  borderRadius: 20, padding: '4px 16px', fontSize: 11, fontWeight: 800,
                  whiteSpace: 'nowrap',
                }}>{plan.badge}</div>
              )}

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: plan.color, marginBottom: 6 }}>{plan.name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  {price !== null ? (
                    <>
                      <span style={{ fontSize: 44, fontWeight: 800, color: C.text, letterSpacing: '-2px' }}>
                        {price === 0 ? 'Gratuit' : `${price}FCFA `}
                      </span>
                      {price > 0 && <span style={{ fontSize: 14, color: C.muted }}>/mois</span>}
                    </>
                  ) : (
                    <span style={{ fontSize: 28, fontWeight: 800, color: C.text }}>Sur devis</span>
                  )}
                </div>
                {billing === 'yearly' && plan.yearlyPrice && (
                  <div style={{ fontSize: 11, color: C.accent, marginTop: 4 }}>
                    Facturé {plan.yearlyPrice}FCFA /an — économisez {Math.round(plan.price * 12 - plan.yearlyPrice)}FCFA 
                  </div>
                )}
                <div style={{ marginTop: 12, display: 'inline-block', background: `${plan.color}22`, border: `1px solid ${plan.color}44`, borderRadius: 8, padding: '3px 10px', fontSize: 12, color: plan.color, fontWeight: 700 }}>
                  Commission transactions: {plan.fee}
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20, marginBottom: 24 }}>
                {plan.features.map((f, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ color: plan.color, fontSize: 14, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 13, color: C.text }}>{f}</span>
                  </div>
                ))}
                {plan.missing?.map((f, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, opacity: 0.4 }}>
                    <span style={{ color: C.muted, fontSize: 14, flexShrink: 0 }}>✗</span>
                    <span style={{ fontSize: 13, color: C.muted }}>{f}</span>
                  </div>
                ))}
              </div>

              {plan.note && (
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 16, fontStyle: 'italic' }}>* {plan.note}</div>
              )}

              <button onClick={() => handleChoosePlan(plan)} style={{
                width: '100%', padding: '14px',
                background: plan.highlight ? `linear-gradient(90deg, ${plan.color}, ${plan.color}cc)` : 'transparent',
                border: `2px solid ${plan.color}`,
                borderRadius: 12, cursor: 'pointer',
                color: plan.highlight ? (plan.color === C.muted ? '#fff' : '#000') : plan.color,
                fontWeight: 800, fontSize: 14, fontFamily: "'Sora', sans-serif",
                transition: 'all 0.2s',
              }}>
                {plan.cta || (plan.code === 'free' ? 'Commencer gratuitement' : `Choisir ${plan.name}`)}
              </button>
            </div>
          );
        })}
      </div>

      {/* Projections revenus */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px 80px' }}>
        <h2 style={{ textAlign: 'center', fontSize: 32, fontWeight: 800, marginBottom: 8, letterSpacing: '-1px' }}>
          📈 Projections de revenus
        </h2>
        <p style={{ textAlign: 'center', color: C.muted, marginBottom: 40 }}>
          Basées sur 1% de commission + abonnements
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {REVENUE_PROJECTIONS.map((proj, i) => (
            <div key={i} style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 16, padding: '20px 20px',
              borderTop: `3px solid ${[C.muted, C.blue, C.accent, C.gold][i]}`,
            }}>
              <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{proj.label}</div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>{proj.users.toLocaleString()} utilisateurs actifs</div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>Volume: {proj.volume}/mois</div>
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: C.muted }}>Commissions</span>
                  <span style={{ fontSize: 12, color: C.accent, fontWeight: 600 }}>{proj.fees}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: C.muted }}>Abonnements</span>
                  <span style={{ fontSize: 12, color: C.blue, fontWeight: 600 }}>{proj.subs}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Total/mois</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: [C.muted, C.blue, C.accent, C.gold][i] }}>{proj.total}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: C.muted }}>
          Projections indicatives. Les coûts fixes serveur restent ~17FCFA /mois quel que soit le volume.
        </div>
      </div>
    </div>
  );
}
